import { Typography, useTheme } from '@mui/material';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { LISTED_CATEGORIES_QUESTION } from '@/constants/document';
import { EMPLOYMENT_STATUS_TEXT, PROFESSION_TEXT } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';

import { EmploymentStatus, Profession, User } from '@/types/user';

import { formatDateValue, getDisplayedPhone } from '@/utils/string';
import { getOccupationName, isCaymanUser } from '@/utils/user';

import { InfoReviewDetailCard } from './styled';

type EmploymentDetailCardProps = {
  user: User;
};

export function EmploymentDetailCard({
  user,
}: Readonly<EmploymentDetailCardProps>) {
  const { t } = useTranslate();
  const { occupations, directorOccupations, businessCategoriesById } =
    useAppSelector(selectConfigsState);
  const { dateFormat } = useAppSelector(selectConfigsState);

  const employmentType = t(
    PROFESSION_TEXT[user.employmentDetailed?.employmentDesc as Profession],
  );

  const employmentStatus = user.employmentDetailed?.employmentStatus;

  return (
    <InfoReviewDetailCard>
      <Typography variant="h5" paddingBottom={1}>
        {t('reviewPersonalDetails.employmentDetails.title')}
      </Typography>
      <Divider width={'100%'} />
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.employmentDetails.field.employmentStatus')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employmentStatus ? t(EMPLOYMENT_STATUS_TEXT[employmentStatus]) : ''}
      </InfoReviewFieldValue>

      {employmentStatus === EmploymentStatus.Employed && (
        <>
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.employmentDetails.field.employer')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user.employmentDetailed?.employerName}
          </InfoReviewFieldValue>
          <Typography variant="body2Medium">
            {user.employmentDetailed?.employerAddress}
          </Typography>
          <Typography variant="body2Medium">
            {getDisplayedPhone(user.employmentDetailed?.employerPhone)}
          </Typography>
          {isCaymanUser(user) && (
            <>
              <InfoReviewFieldLabel>
                {t(
                  'reviewPersonalDetails.employmentDetails.field.businessCategory',
                )}
              </InfoReviewFieldLabel>
              <InfoReviewFieldValue>
                {
                  businessCategoriesById[
                    user.employmentDetailed?.businessCategoryCode || ''
                  ]?.text
                }
              </InfoReviewFieldValue>
            </>
          )}
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.employmentDetails.field.occupation')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getOccupationName({
              user,
              occupations,
              directorOccupations,
            })}
          </InfoReviewFieldValue>
          <InfoReviewFieldLabel sx={{ mt: 0.5 }}>
            {t('reviewPersonalDetails.employmentDetails.field.since', {
              date: formatDateValue(
                user.employmentDetailed?.startDate || '',
                dateFormat,
              ),
            })}
          </InfoReviewFieldLabel>
        </>
      )}
      {employmentStatus === EmploymentStatus.SelfEmployed &&
        (isCaymanUser(user) ? (
          <CaymanSelfEmploymentDetail user={user} />
        ) : (
          <SelfEmploymentDetail user={user} />
        ))}
      {employmentStatus === EmploymentStatus.Unemployed && (
        <>
          <InfoReviewFieldLabel>
            {t(
              'reviewPersonalDetails.employmentDetails.field.unemploymentType',
            )}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{employmentType}</InfoReviewFieldValue>
        </>
      )}
    </InfoReviewDetailCard>
  );
}

function SelfEmploymentDetail({ user }: Readonly<EmploymentDetailCardProps>) {
  const { t } = useTranslate();

  const { showSelfEmployedStartupQuestion } =
    useAppSelector(selectConfigsState);
  const theme = useTheme();

  const employmentType = t(
    PROFESSION_TEXT[user.employmentDetailed?.employmentDesc as Profession],
  );

  const riskSectorFlag = user.employmentDetailed?.riskSectorFlag;

  const startUpFlag = user.employmentDetailed?.isStartup;

  return (
    <>
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.employmentDetails.field.label')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {t(LISTED_CATEGORIES_QUESTION.description)}
      </InfoReviewFieldValue>
      <InfoReviewFieldValue>
        {t('reviewPersonalDetails.employmentDetails.field.answer')}
        <Typography
          variant="body2Medium"
          component="span"
          mt={1}
          color={
            riskSectorFlag
              ? theme.palette.semantic.error[500]
              : theme.palette.semantic.success[500]
          }
        >
          {riskSectorFlag ? t('common.yes') : t('common.no')}
        </Typography>
      </InfoReviewFieldValue>
      {showSelfEmployedStartupQuestion && (
        <>
          <InfoReviewFieldLabel>
            {t('reviewPersonalDetails.employmentDetails.field.question')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t('reviewPersonalDetails.employmentDetails.field.answer')}
            <Typography
              variant="body2Medium"
              component="span"
              mt={1}
              color={
                startUpFlag
                  ? theme.palette.semantic.error[500]
                  : theme.palette.semantic.success[500]
              }
            >
              {startUpFlag ? t('common.yes') : t('common.no')}
            </Typography>
          </InfoReviewFieldValue>
        </>
      )}

      {!riskSectorFlag && (
        <>
          <InfoReviewFieldLabel>
            {t(
              'reviewPersonalDetails.employmentDetails.field.selfEmploymentType',
            )}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{employmentType}</InfoReviewFieldValue>
        </>
      )}
    </>
  );
}

function CaymanSelfEmploymentDetail({
  user,
}: Readonly<EmploymentDetailCardProps>) {
  const { t } = useTranslate();

  const { occupations, directorOccupations, businessCategoriesById } =
    useAppSelector(selectConfigsState);

  return (
    <>
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.employmentDetails.field.businessName')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {user.employmentDetailed?.employerName}
      </InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.employmentDetails.field.businessCategory')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {
          businessCategoriesById[
            user.employmentDetailed?.businessCategoryCode || ''
          ]?.text
        }
      </InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        {t('reviewPersonalDetails.employmentDetails.field.occupation')}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {getOccupationName({
          user,
          occupations,
          directorOccupations,
        })}
      </InfoReviewFieldValue>
    </>
  );
}
